import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getMarketplaceApps,
  getMarketplaceAppDetail,
  cpInstallApp,
  cpRemoveApp,
  type AvailableApp,
} from "@/lib/api/controlPlaneClient";
import { useAuthStore } from "@/lib/store/authStore";

export type { AvailableApp };

/** Full detail for a single app — used by the detail page (Epic 03) */
export interface AppDetail extends AvailableApp {
  long_description?: string;
  video_url?: string;
  pricing_description?: string;
  review_count?: number;
  developer_name?: string;
  works_with?: string;
  screenshots: Array<{ image_url: string; caption?: string }>;
  key_features: Array<{ icon?: string; title: string; description: string }>;
}

export function useAppDetail(appName: string | undefined) {
  return useQuery<AppDetail>({
    queryKey: ["apps", "detail", appName],
    queryFn: () => getMarketplaceAppDetail(appName!),
    enabled: !!appName,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAvailableApps() {
  return useQuery<AvailableApp[]>({
    queryKey: ["apps", "marketplace"],
    queryFn: getMarketplaceApps,
    staleTime: 5 * 60 * 1000, // 5 min — catalogue rarely changes
  });
}

export function useInstallApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appName: string) => cpInstallApp(appName),
    onMutate: (appName) => {
      const prev = useAuthStore.getState().installedApps;
      useAuthStore.setState({
        installedApps: [...prev, appName],
      });
      return { prev };
    },
    onError: (_err, _appName, context) => {
      if (context?.prev) {
        useAuthStore.setState({ installedApps: context.prev });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["apps", "marketplace"] });
    },
  });
}

export function useRemoveApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appName: string) => cpRemoveApp(appName),
    onMutate: (appName) => {
      const prev = useAuthStore.getState().installedApps;
      useAuthStore.setState({
        installedApps: prev.filter((a) => a !== appName),
      });
      return { prev };
    },
    onError: (_err, _appName, context) => {
      if (context?.prev) {
        useAuthStore.setState({ installedApps: context.prev });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["apps", "marketplace"] });
    },
  });
}
